import React from 'react';
import { Card, Button } from 'antd';
import {
    CheckCircleFilled,
    VideoCameraOutlined,
    ClockCircleOutlined,
    StarFilled,
    RedoOutlined,
    UserOutlined,
    RightOutlined
} from '@ant-design/icons';

const InterviewComplete = ({ onContinue = () => true, onRetake = () => true, onProfile = () => true }) => {
    return (
        <Card className="w-full max-w-2xl mx-auto">
            <div className="p-8">
                {/* Success Header */}
                <div className="flex flex-col items-center mb-8">
                    <div className="relative mb-4">
                        <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircleFilled className="text-4xl text-green-600" />
                        </div>
                        {/* Decorative stars */}
                        <StarFilled className="text-xl text-amber-400 absolute -top-2 -right-1 animate-pulse" />
                        <StarFilled className="text-base text-amber-400 absolute top-2 -left-1 animate-pulse delay-75" />
                    </div>
                    <h2 className="text-2xl font-semibold text-gray-900 text-center mb-2">
                        Video Interview Complete!
                    </h2>
                    <p className="text-gray-600 text-center max-w-md">
                        You've successfully recorded all your responses. Take a moment to review them before moving forward.
                    </p>
                </div>

                {/* Response Summary */}
                <div className="bg-gray-50 rounded-lg p-6 mb-8">
                    <div className="grid grid-cols-2 gap-6">
                        {/* Stats */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                                    <VideoCameraOutlined className="text-lg text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Responses Recorded</p>
                                    <p className="text-lg font-semibold text-gray-900">5 Videos</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <ClockCircleOutlined className="text-lg text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Total Length of Response</p>
                                    <p className="text-lg font-semibold text-gray-900">12:35</p>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="border-l border-gray-200 pl-6">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h3>
                            <ul className="space-y-2">
                                <li>
                                    <Button
                                        type="link"
                                        onClick={onRetake}
                                        className="text-purple-600 hover:text-purple-700 flex items-center gap-2 text-sm p-0 h-auto"
                                        icon={<RedoOutlined />}
                                    >
                                        Retake any response
                                    </Button>
                                </li>
                                <li>
                                    <Button
                                        type="link"
                                        onClick={onProfile}
                                        className="text-gray-600 hover:text-gray-700 flex items-center gap-2 text-sm p-0 h-auto"
                                        icon={<UserOutlined />}
                                    >
                                        Return to profile
                                    </Button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Tips Section */}
                <div className="mb-8">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Before you continue:</h3>
                    <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                            <div className="h-5 w-5 flex items-center justify-center mt-0.5">
                                <div className="h-1.5 w-1.5 rounded-full bg-purple-600"></div>
                            </div>
                            <span className="text-gray-600">Review your responses for clarity and professionalism</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <div className="h-5 w-5 flex items-center justify-center mt-0.5">
                                <div className="h-1.5 w-1.5 rounded-full bg-purple-600"></div>
                            </div>
                            <span className="text-gray-600">Check that your audio is clear and background is appropriate</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <div className="h-5 w-5 flex items-center justify-center mt-0.5">
                                <div className="h-1.5 w-1.5 rounded-full bg-purple-600"></div>
                            </div>
                            <span className="text-gray-600">You can retake any response if you're not satisfied</span>
                        </li>
                    </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 justify-end">
                    <Button
                        onClick={onRetake}
                        className="border-gray-200 text-gray-600 hover:text-gray-700 hover:border-gray-300"
                    >
                        Review & Retake
                    </Button>
                    <Button
                        type="primary"
                        onClick={onContinue}
                        className="bg-purple-600 hover:bg-purple-700 border-purple-600 hover:border-purple-700 flex items-center"
                        icon={<RightOutlined />}
                    >
                        Continue
                    </Button>
                </div>
            </div>

            <style jsx global>{`
        .ant-card {
          border-radius: 0.5rem;
        }
        .ant-card-body {
          padding: 0;
        }
        .ant-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .ant-btn-primary {
          background: #9333ea;
        }
        .ant-btn-primary:hover {
          background: #7e22ce !important;
        }
        .ant-btn-link {
          display: inline-flex !important;
          padding: 0 !important;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
        </Card>
    );
};

export default InterviewComplete;